import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toastOk, toastErro, msgErro, confirmar } from '../../services/ui';
import { useSort } from '../../helpers/useSort';
import { useAuth } from '../../contexts/AuthContext';

// Caixa que mostra a senha provisória UMA vez, com botão de copiar
function SenhaProvisoria({ senha, onFechar }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="card p-3 mb-3" style={{ borderLeft: '4px solid var(--accent)', background: 'var(--accent-soft)' }}>
      <div className="fw-bold mb-1"><i className="bi bi-key me-1" /> Senha provisória gerada</div>
      <div className="mb-2" style={{ fontSize: '0.85rem', color: 'var(--text-suave)' }}>
        Copie e repasse ao fornecedor — ela <b>não será exibida de novo</b>. No primeiro acesso o sistema pede troca.
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <code style={{ fontSize: '1.1rem', background: '#fff', padding: '8px 14px', borderRadius: 8, border: '1px solid var(--borda)', letterSpacing: '1px' }}>{senha}</code>
        <button className="btn-outline-for" onClick={() => {
          const p = navigator.clipboard?.writeText(senha);
          if (p) p.then(() => setCopiado(true)).catch(() => toastErro('Não consegui copiar. Selecione o código e copie manualmente.'));
          else toastErro('Seu navegador não permite copiar automaticamente. Selecione o código e copie manualmente.');
        }}>
          {copiado ? 'Copiado!' : 'Copiar'}
        </button>
        <button className="btn btn-sm btn-link p-0 ms-auto" onClick={onFechar}>Fechar</button>
      </div>
    </div>
  );
}

export default function Usuarios() {
  const { fornecedorId } = useParams();
  const navigate = useNavigate();
  const { temPermissao, verComo } = useAuth();
  // "Ver como": abre o portal com a visão deste usuário (somente leitura, 1h, registrado no log).
  const abrirComo = async (u) => {
    try { await verComo(u.id); navigate('/'); }
    catch (e) { toastErro(msgErro(e, 'Não deu pra abrir a visão desse usuário.')); }
  };
  const [fornecedor, setFornecedor] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState({ username: '', nome: '', email: '' });
  const [salvando, setSalvando] = useState(false);
  const [provisoria, setProvisoria] = useState(null);
  const [todosForn, setTodosForn] = useState([]);
  const [editando, setEditando] = useState(null); // { id, username, nome, email, cnpjs:[...] }
  const s = useSort('nome');

  const carregar = async () => {
    try {
      const [fs, us] = await Promise.all([
        api.get('/fornecedores'),
        api.get('/usuarios', { params: { fornecedor_id: fornecedorId } }),
      ]);
      setFornecedor(fs.data.find((f) => String(f.id) === String(fornecedorId)) || null);
      setTodosForn(fs.data);
      setUsuarios(us.data);
    } catch (e) {
      toastErro(msgErro(e, 'Erro ao carregar.'));
    } finally { setCarregando(false); }
  };
  useEffect(() => { carregar(); }, [fornecedorId]);

  const criar = async (e) => {
    e.preventDefault();
    if (salvando) return;
    setSalvando(true); setProvisoria(null);
    try {
      const { data } = await api.post('/usuarios', { ...form, fornecedor_id: Number(fornecedorId) });
      setProvisoria(data.senha_provisoria);
      setForm({ username: '', nome: '', email: '' });
      await carregar();
      toastOk('Usuário criado.');
    } catch (e) {
      toastErro(msgErro(e, 'Erro ao criar usuário.'));
    } finally { setSalvando(false); }
  };

  const resetar = async (u) => {
    const ok = await confirmar({
      titulo: `Gerar nova senha para ${u.username}?`,
      texto: 'A senha atual dele deixa de funcionar imediatamente.',
      confirmarTexto: 'Gerar nova senha',
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/usuarios/${u.id}/resetar-senha`);
      setProvisoria(data.senha_provisoria);
      toastOk('Senha provisória gerada.');
    } catch (e) { toastErro(msgErro(e)); }
  };

  const toggleAtivo = async (u) => {
    try {
      await api.patch(`/usuarios/${u.id}`, { nome: u.nome, email: u.email, ativo: !u.ativo });
      setUsuarios((l) => l.map((x) => (x.id === u.id ? { ...x, ativo: !x.ativo } : x)));
      toastOk(u.ativo ? 'Usuário desativado.' : 'Usuário ativado.');
    } catch (e) { toastErro(msgErro(e)); }
  };

  const abrirEdicao = (u) => setEditando({ id: u.id, username: u.username, nome: u.nome, email: u.email || '', cnpjs: u.cnpjs || [] });

  const salvarEdicao = async () => {
    if (!editando.nome.trim()) return toastErro('O nome é obrigatório.');
    try {
      const atual = usuarios.find((x) => x.id === editando.id);
      await api.patch(`/usuarios/${editando.id}`, { nome: editando.nome.trim(), email: editando.email.trim() || null, ativo: atual?.ativo ? 1 : 0 });
      toastOk('Usuário atualizado.');
      setEditando(null);
      await carregar();
    } catch (e) { toastErro(msgErro(e)); }
  };

  const excluir = async (u) => {
    const ok = await confirmar({
      titulo: `Excluir ${u.username} definitivamente?`,
      texto: 'Essa ação não pode ser desfeita — o usuário e seus vínculos serão removidos. (Se quiser só tirar o acesso, use Desativar.)',
      confirmarTexto: 'Excluir de vez', perigo: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/usuarios/${u.id}`);
      toastOk('Usuário excluído.');
      setEditando(null);
      await carregar();
    } catch (e) { toastErro(msgErro(e)); }
  };

  const vincularCnpj = async (fid) => {
    if (!fid) return;
    try {
      await api.post(`/usuarios/${editando.id}/cnpjs`, { fornecedor_id: Number(fid) });
      const f = todosForn.find((x) => x.id === Number(fid));
      setEditando((e) => ({ ...e, cnpjs: [...e.cnpjs, { fornecedor_id: Number(fid), nome: f?.nome, cnpj: f?.cnpj }] }));
      toastOk('CNPJ vinculado.');
      await carregar();
    } catch (e) { toastErro(msgErro(e)); }
  };

  const desvincularCnpj = async (fid) => {
    try {
      await api.delete(`/usuarios/${editando.id}/cnpjs/${fid}`);
      setEditando((e) => ({ ...e, cnpjs: e.cnpjs.filter((c) => c.fornecedor_id !== fid) }));
      toastOk('CNPJ desvinculado.');
      await carregar();
    } catch (e) { toastErro(msgErro(e)); }
  };

  return (
    <div>
      <button className="btn btn-sm btn-link p-0 mb-2" onClick={() => navigate('/admin/fornecedores')}>
        <i className="bi bi-arrow-left me-1" /> Voltar aos fornecedores
      </button>
      <h4 className="fw-bold mb-1">
        <i className="bi bi-people me-2" style={{ color: 'var(--primary)' }} />
        Usuários {fornecedor && <span style={{ color: 'var(--primary)' }}>· {fornecedor.nome}</span>}
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
        {fornecedor?.cnpj && `CNPJ ${fornecedor.cnpj} · `}Um CNPJ pode ter vários usuários.
      </p>

      {provisoria && <SenhaProvisoria senha={provisoria} onFechar={() => setProvisoria(null)} />}

      {/* Form de criação — grid Bootstrap (empilha no mobile) */}
      <div className="card p-3 mb-4">
        <div className="fw-bold mb-3">Novo usuário</div>
        <form onSubmit={criar} className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="fw-semibold mb-1" style={{ fontSize: '0.82rem' }}>Usuário (login)</label>
            <input className="form-control" required value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="ex.: joao.tambasa" />
          </div>
          <div className="col-12 col-md-3">
            <label className="fw-semibold mb-1" style={{ fontSize: '0.82rem' }}>Nome</label>
            <input className="form-control" required value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome do contato" />
          </div>
          <div className="col-12 col-md">
            <label className="fw-semibold mb-1" style={{ fontSize: '0.82rem' }}>
              E-mail <span className="text-muted fw-normal">(opcional)</span>
            </label>
            <input className="form-control" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="para recuperar senha" />
          </div>
          <div className="col-12 col-md-auto">
            <button className="btn-primary-for w-100" disabled={salvando}>{salvando ? '...' : 'Criar'}</button>
          </div>
        </form>
      </div>

      {/* Lista */}
      {carregando ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} role="status" /></div>
      ) : usuarios.length === 0 ? (
        <div className="text-center text-muted py-4">Nenhum usuário ainda. Crie o primeiro acima.</div>
      ) : (
        <div className="card p-0">
          <div className="table-responsive">
            <table className="table table-hover table-for mb-0 align-middle">
              <thead>
                <tr>
                  <th onClick={() => s.toggle('username')}>Usuário {s.icon('username')}</th>
                  <th onClick={() => s.toggle('nome')}>Nome {s.icon('nome')}</th>
                  <th onClick={() => s.toggle('email')}>E-mail {s.icon('email')}</th>
                  <th className="text-center" onClick={() => s.toggle('ativo')}>Status {s.icon('ativo')}</th>
                  <th className="nao-ord"><span className="visually-hidden">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {s.aplicar(usuarios, (r, k) => (k === 'ativo' ? (r.ativo ? 1 : 0) : r[k])).map((u) => (
                  <tr key={u.id}>
                    <td className="fw-semibold">{u.username}</td>
                    <td>
                      {u.nome}
                      {u.cnpjs && u.cnpjs.length > 1 && (
                        <span className="badge-filial ms-2" title={u.cnpjs.map((c) => c.nome).join(', ')}>
                          <i className="bi bi-diagram-3 me-1" />{u.cnpjs.length} CNPJs
                        </span>
                      )}
                    </td>
                    <td>{u.email || '—'}</td>
                    <td className="text-center">
                      <span className={u.ativo ? 'badge-positivo' : 'badge-negativo'}>{u.ativo ? 'Ativo' : 'Inativo'}</span>
                      {u.senha_provisoria ? <span className="badge-pendente ms-1">senha provisória</span> : null}
                    </td>
                    <td className="text-end text-nowrap">
                      {temPermissao('ver_como_fornecedor') && u.ativo ? (
                        <button className="btn btn-sm btn-link p-0 me-3" onClick={() => abrirComo(u)}
                          title="Abre o portal exatamente como este usuário vê (somente leitura)">
                          <i className="bi bi-eye me-1" />Ver como
                        </button>
                      ) : null}
                      <button className="btn btn-sm btn-link p-0 me-3" onClick={() => abrirEdicao(u)}>Editar</button>
                      <button className="btn btn-sm btn-link p-0 me-3" onClick={() => resetar(u)}>Resetar senha</button>
                      <button className="btn btn-sm btn-link p-0 me-3" onClick={() => toggleAtivo(u)}>{u.ativo ? 'Desativar' : 'Ativar'}</button>
                      <button className="btn btn-sm btn-link p-0 text-danger" onClick={() => excluir(u)}>Excluir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editando && (
        <div className="uf-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEditando(null); }}>
          <div className="uf-modal">
            <div className="uf-head">
              <div>
                <div style={{ fontWeight: 700 }}>Editar usuário</div>
                <div className="text-muted" style={{ fontSize: '.82rem' }}>@{editando.username}</div>
              </div>
              <button className="uf-x" onClick={() => setEditando(null)}><i className="bi bi-x-lg" /></button>
            </div>
            <div className="uf-body">
              <label className="fw-semibold mb-1" style={{ fontSize: '.82rem' }}>Nome</label>
              <input className="form-control mb-3" value={editando.nome} onChange={(e) => setEditando({ ...editando, nome: e.target.value })} />
              <label className="fw-semibold mb-1" style={{ fontSize: '.82rem' }}>E-mail <span className="text-muted fw-normal">(recuperação de senha)</span></label>
              <input className="form-control mb-3" type="email" value={editando.email} onChange={(e) => setEditando({ ...editando, email: e.target.value })} placeholder="voce@empresa.com.br" />

              <div className="fw-semibold" style={{ fontSize: '.82rem' }}>CNPJs que este usuário acessa</div>
              <div className="text-muted mb-2" style={{ fontSize: '.78rem' }}>Ele vê os dados de todos os CNPJs abaixo, juntos.</div>
              <div className="uf-cnpjs">
                {editando.cnpjs.map((c) => (
                  <div key={c.fornecedor_id} className="uf-cnpj">
                    <span><i className="bi bi-building me-2" style={{ color: 'var(--primary)' }} />{c.nome}{c.cnpj ? ` · ${c.cnpj}` : ''}</span>
                    <button className="uf-cnpj-x" disabled={editando.cnpjs.length <= 1}
                      title={editando.cnpjs.length <= 1 ? 'Precisa de ao menos um CNPJ' : 'Remover CNPJ'}
                      onClick={() => desvincularCnpj(c.fornecedor_id)}><i className="bi bi-x-circle" /></button>
                  </div>
                ))}
              </div>
              <select className="form-select mt-2" value="" onChange={(e) => { vincularCnpj(e.target.value); e.target.value = ''; }}>
                <option value="">+ Vincular outro CNPJ...</option>
                {todosForn.filter((f) => !editando.cnpjs.some((c) => c.fornecedor_id === f.id)).map((f) => (
                  <option key={f.id} value={f.id}>{f.nome}{f.cnpj ? ` · ${f.cnpj}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="uf-foot">
              <button className="btn btn-sm btn-link text-danger p-0" onClick={() => excluir(editando)}><i className="bi bi-trash me-1" />Excluir usuário</button>
              <div>
                <button className="btn btn-sm btn-link me-2" onClick={() => setEditando(null)}>Cancelar</button>
                <button className="btn-primary-for" onClick={salvarEdicao}>Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .uf-overlay { position: fixed; inset: 0; background: rgba(16,36,26,.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .uf-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 520px; max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 24px 60px rgba(0,0,0,.32); overflow: hidden; }
        .uf-head { padding: 16px 20px; border-bottom: 1px solid var(--borda); display: flex; align-items: flex-start; justify-content: space-between; }
        .uf-x { background: none; border: none; color: var(--muted); font-size: 1.1rem; cursor: pointer; }
        .uf-body { padding: 18px 20px; overflow: auto; }
        .uf-cnpjs { display: flex; flex-direction: column; gap: 6px; }
        .uf-cnpj { display: flex; align-items: center; justify-content: space-between; gap: 10px; background: #f4f7f4; border: 1px solid var(--borda); border-radius: 10px; padding: 9px 12px; font-size: .86rem; }
        .uf-cnpj-x { background: none; border: none; color: var(--danger); cursor: pointer; font-size: 1rem; padding: 0; }
        .uf-cnpj-x:disabled { color: #c6cfc7; cursor: default; }
        .uf-foot { padding: 14px 20px; border-top: 1px solid var(--borda); display: flex; align-items: center; justify-content: space-between; }
      `}</style>
    </div>
  );
}
